import { Component, EventEmitter, Output } from '@angular/core';
import * as Cesium from 'cesium';
import { IllumPolygonCreatorService } from '../../../../core/services/Illum-polygon-creator.service';
import { IncidentMapEntity } from '../../../../core/services/incident-map-entity.service';
import { CommonModule } from '@angular/common';
import { CreatorDialogComponent } from './creator-dialog/creator-dialog.component';
import { IncidentCreatorService } from '../../../../core/services/wrench-services/incident-creator.service';
import { DataContextService } from '../../../../core/services/data-context.service';
import { CoreService } from '../../../../core/services/core.service';
import { SafeZoneMapEntity } from '../../../../core/services/safe-zone-map-entity.service';
import { PolygonDrawingHelper } from '../../../../core/services/polygon-drawing-helper.service';
import { GlobeService } from '../../../../core/services/globe.service';
import { PluginContextService } from '../../../../core/services/plugin-context.service';
import { FocalPointMapEntity } from '../../../../core/services/focal-point-map-entity.service';
import { CameraService } from '../../../../core/services/camera.service';
import { GlobalSettingsService } from '../../../../core/services/global-settings.service';
import { WindowService } from '../../../../core/services/window.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TimelineService } from '../../../../core/services/timeline.service';

@Component({
  selector: 'app-incident-creator-button',
  standalone: true,
  imports: [CommonModule, CreatorDialogComponent],
  templateUrl: './incident-creator-button.component.html',
  styleUrl: './incident-creator-button.component.css'
})
export class IncidentCreatorButtonComponent {
  @Output() drawingCompleted = new EventEmitter<any>();
  @Output() drawingCancelled = new EventEmitter<void>();
  @Output() toolbarClosed = new EventEmitter<void>();
  @Output() toolState = new EventEmitter<boolean>();

  items = ['drawshape', 'focalpoint', 'addpoint'] as const;
  selection: typeof this.items[number] = this.items[0];
  type: 'normal' | 'muster' = 'normal';
  focalPoint: any = undefined;
  focalPointText = 'Set Focal Point';
  focalPointDivMessage = 'In order to set focal point click on <strong>Set Focal Point</strong> or <strong>Continue</strong> to skip.';

  private tempFocalPoint: any;
  showCreatorDialog = false;
  creatorDialogData: any = null;
  private polygonHelper: PolygonDrawingHelper | null = null;
  isToolActive = false;
  
  constructor(
    private illumPolygonCreatorService: IllumPolygonCreatorService,
    private incidentCreatorService: IncidentCreatorService,
    private dataContext: DataContextService,
    private core: CoreService,
    private globeService: GlobeService,
    private pluginContext: PluginContextService,
    private cameraService : CameraService,
    private globalSettings: GlobalSettingsService,
    private windowService: WindowService,
    private modalService: NgbModal,
    private timelineService: TimelineService
  ) {}

  ngOnInit(): void {
    // Remove any globe click listeners for focal point
    this.pluginContext.removeGlobeClickListener('IncidentCreatorButton');
    this.illumPolygonCreatorService.startDrawing({
      callback: () => this.finishedDrawClicked()
    });
    this.selection = 'drawshape';
  }

  ngAfterViewInit(): void {
    const viewer = this.globeService.getViewer();
    if (!viewer) {
      // Optionally, retry or show an error
      console.error('Cesium viewer is not ready.');
      return;
    }
    this.polygonHelper = new PolygonDrawingHelper(viewer);
    this.polygonHelper.startDrawing();
  }

  cancelDrawClicked(): void {
    this.illumPolygonCreatorService.stopDrawing();
    this.drawingCancelled.emit();
    this.cleanup();
    this.toolbarClosed.emit();
    this.isToolActive = false;
    this.toolState.emit(false);
  }

  finishedDrawClicked(): void {
  this.illumPolygonCreatorService.freezeDrawing();
  this.polygonHelper?.finishDrawing();
  this.selection = 'focalpoint';
}
  // When starting polygon drawing
  startPolygonDrawing() {
    this.core.removeGlobeClickListener('IncidentCreatorButton');
    this.illumPolygonCreatorService.startDrawing({
      callback: () => this.finishedDrawClicked()
    });
    this.selection = 'drawshape';
  }

setFocalPointButtonClicked(): void {
  this.selection = 'addpoint';
  this.core.setToolActive(true);
  this.pluginContext.removeGlobeClickListener('IncidentCreatorButton');
  this.pluginContext.addGlobeClickListener('IncidentCreatorButton', (lat: any, lon: any) => {
    if (this.tempFocalPoint) {
      this.dataContext.removeMapEntity(this.tempFocalPoint);
      this.tempFocalPoint = undefined;
    }
    this.tempFocalPoint = new FocalPointMapEntity({ type: 'Point', coordinates: [lon, lat] }, new Date(), this.cameraService, this.core, this.windowService, this.dataContext, this.modalService, this.timelineService);
    this.dataContext.addMapEntity(this.tempFocalPoint);
    this.focalPoint = { type: 'Point', coordinates: [lon, lat] };
    this.finishedFocalPointClicked();
    this.core.setToolActive(false);
    this.pluginContext.removeGlobeClickListener('IncidentCreatorButton');
  }); 
}

  continueButtonClicked(): void {
    const positions = this.polygonHelper?.getPositions();
    if (!positions || positions.length < 3) {
      return;
    }
    this.polygonHelper?.finishDrawing();

    const polypositions = this.illumPolygonCreatorService.getPolygon();
    if (!polypositions || polypositions.length < 3) {
      return;
    }
    this.finishDrawing(polypositions);
  }

  finishedFocalPointClicked(): void {
    this.selection = 'focalpoint';
    this.focalPointText = 'Reset Focal Point';
    this.focalPointDivMessage = 'In order to Reset focal point click on <strong>Reset Focal Point</strong> or <strong>Continue</strong> to skip.';
  }

  cancelFocalPointClicked(): void {
    this.selection = 'focalpoint';
    this.focalPoint = undefined;
  }

  private finishDrawing(polypositions: any[]): void {
    const cartographicPositions = polypositions.map(pos => {
      const carto = Cesium.Ellipsoid.WGS84.cartesianToCartographic(pos);
      return {
        lat: Cesium.Math.toDegrees(carto.latitude),
        lon: Cesium.Math.toDegrees(carto.longitude)
      };
    });

    let name = this.type === 'muster' ? 'New Geofence' : 'New Incident';

    const incidentObj: any = {
      id: this.guid(),
      label: name,
      highGeometries: [{ coordinates: [cartographicPositions] }],
      updatedAt: new Date(),
      createdAt: new Date(),
      status: IncidentMapEntity.PENDING_STATE,
      description: ''
    };

    const incidentEntity = new IncidentMapEntity(incidentObj, this.dataContext,this.cameraService,this.core,this.globalSettings,this.windowService,this.modalService,this.timelineService, false);

    if (this.type === 'muster') {
      incidentEntity.setIsMuster(true);
    }
    if (this.focalPoint) {
      incidentEntity.focalPointGeo = this.focalPoint;
    }

    this.creatorDialogData = {
      data: incidentEntity,
      entitytype: 'Incident',
      IncidentMapEntity: IncidentMapEntity,
      SafeZoneMapEntity: SafeZoneMapEntity
    };
    this.showCreatorDialog = true;
    this.isToolActive = true;
    this.toolState.emit(true);
  }

  private guid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  private cleanup(): void {
    this.pluginContext.removeGlobeClickListener('IncidentCreatorButton');
    if (this.tempFocalPoint) {
      this.dataContext.removeMapEntity(this.tempFocalPoint);
      this.tempFocalPoint = undefined;
    }
    
    if (this.polygonHelper) {
      this.polygonHelper.clear();
      this.polygonHelper = null;
    }
  }
  creatorDialogClosed(result: any) {
    this.showCreatorDialog = false;
    this.cleanup();
    this.onCloseClick();
    this.isToolActive = false;
    this.toolState.emit(false);
    if (result) {
      this.drawingCompleted.emit();
    } else {
      this.drawingCancelled.emit();
    }
}
  onCloseClick(): void {
     if (this.tempFocalPoint) {
      this.dataContext.removeMapEntity(this.tempFocalPoint);
      this.tempFocalPoint = undefined;
    }
    
    this.pluginContext.removeGlobeClickListener('IncidentCreatorButton');
    if (this.polygonHelper) {
      this.polygonHelper.clear();
      this.polygonHelper = null;
    }
    if (this.illumPolygonCreatorService) {
      this.illumPolygonCreatorService?.clearDrawing();
    }
    this.toolbarClosed.emit();
  }
  ngOnDestroy(): void {
    if (this.polygonHelper) {
      this.polygonHelper.clear();
      this.polygonHelper = null;
    }
    if (this.illumPolygonCreatorService) {
      this.illumPolygonCreatorService?.clearDrawing();
    }
  }
}